import {Trans} from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {useRuleEditorContext} from '../../editors/ruleEditor/ruleEditorContext';
import {fromDefinitionAction} from '../ruleActionHelpers';
import {RuleActionDefinition} from '../ruleParameterModel';
import {DropdownGroup} from './dropdowns/multipleGroupsDropdownHelpers';
import {EditorStateActions} from './hooks/useEditorState';
import {RuleActionCard, RuleActionCardProps} from './ruleActionCard';
import {RuleActionsPicker} from './ruleActionsPicker';
import {RuleAddCard} from './ruleAddCard';

/*
 * Types.
 */

interface RuleConditionsListProps {
  actions: ReadonlyArray<RuleActionCardProps>;
  actionGroups: ReadonlyArray<DropdownGroup<RuleActionDefinition>>;
  shouldShowMoreOptions: boolean;
  onAddAction: EditorStateActions['onAddAction'];
}

/*
 * Styles.
 */

const RuleActionsListWrap = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 15px;
`;

/*
 * Component.
 */

export const RuleActionsList = ({
  actions,
  actionGroups,
  shouldShowMoreOptions,
  onAddAction,
}: RuleConditionsListProps) => {
  const {isReadOnly} = useBaseEditorContext();
  const {isLibrary} = useRuleEditorContext();

  const shouldShowAdvanced = !isLibrary || shouldShowMoreOptions;
  const canAddActions = shouldShowAdvanced && !isReadOnly;

  return (
    <RuleActionsListWrap>
      {actions.map(
        ({action, actionDefinition, onUpdateAction, onDeleteAction, shouldHideError, onValidation}) => (
          <RuleActionCard
            key={`rule_action-${action.clientId}`}
            interactionId="rule-editor-action"
            action={action}
            actionDefinition={actionDefinition}
            shouldHideError={shouldHideError}
            onUpdateAction={onUpdateAction}
            onDeleteAction={action.$isReadOnly || !canAddActions ? undefined : onDeleteAction}
            onValidation={onValidation}
          />
        ),
      )}
      {canAddActions && (
        <RuleActionsPicker
          groups={actionGroups}
          onAddAction={(newAction) => onAddAction(fromDefinitionAction(newAction))}
        >
          <RuleAddCard label={<Trans id="rule-editor-action-add">Action</Trans>} />
        </RuleActionsPicker>
      )}
    </RuleActionsListWrap>
  );
};
